import { Injectable, Injector, OnDestroy } from '@angular/core';
import { Router } from '@angular/router';
import { Observable } from 'rxjs';
import { filter } from 'rxjs/operators';
import { generate as shortUid } from 'short-uuid';
import { LoggingService } from '@logging/logging.service';
import { createStore, select, setProps, withProps } from '@ngneat/elf';
import { ActiveSecurityContext } from './state/models/active-security-context';
import { HttpHeaders } from '@angular/common/http';
import { AuthState } from '@auth/state/models/auth.models';
import { FullScreenLoaderService } from '@design-system/loaders/fullscreen-loader.service';
import { UserRole } from '@security/permissions/models/permissions';
import { resolveDefaultPostLoginPath } from './post-login-default-route.util';

//import { environment } from '@env/environment';

const initialState = {fetchingActiveAuthState: false} as ActiveSecurityContext;

/**
 * Provides models management for the active user including their active membership (organisation) /w role(s).
 */
@Injectable({ providedIn: 'root' })
export class ActiveSecurityContextStateService implements OnDestroy {
  /**
   * Router not available from an APP_INITIALIZER (see AuthenticationSetupService > AuthenticationService > SecurityStateService >
   * here), so will locate it directly using the Injector ...
   */
  private router: Router | null = null;

  private readonly store = createStore({ name: 'active-security' }, withProps(initialState));

  /**
   * Observable of the current authenticated user.
   */
  authState$ = this.store.pipe(
    select((s: any) => s.authState),
    filter(Boolean)
  ) as Observable<AuthState>;

  /**
   * Observing the fetching active auth state flag - so that permission-based routes are not resolved until fetched.
   */
  fetchingActiveAuthState$ = this.store.pipe(select((s) => {
    return s.fetchingActiveAuthState
  } ));

  constructor(
    private injector: Injector,
    private logger: LoggingService,
    private fullScreenLoaderService: FullScreenLoaderService
  ) {}

  get authState(): AuthState {
    return this.store.getValue().authState as AuthState;
  }

  /**
   * Gets the current auth state session id (generated for each active browser tab per app load/sign-in or out)
   */
  get sessionId(): string | undefined {
    return this.store.getValue().sessionId;
  }

  setNewSessionId(sessionId: string) {
    this.store.update(setProps({ sessionId, fetchingActiveAuthState: true }));
  }

  /**
   * Appended to api requests. See {@link: JwtInterceptor}
   */
  // eslint-disable-next-line @typescript-eslint/no-unused-vars -- required by interface
  getCAndCApiRequestHeaders(httpHeaders: HttpHeaders): Record<string, string |undefined> {
    const requestId = shortUid();
    this.logger.requestId = requestId;

    const headers = {
      'x-session-id': this.sessionId,
      'x-request-id': requestId,
      'x-timestamp': new Date().getTime().toString()
    };

    this.logger.trace('NEXT API HEADERS: ', headers);
    return headers;
  }

  ensureUnityAccess(): boolean {
    if(this.store.getValue().authState) {   
      return true;
    }
    else {
      this.logger.trace('ensureUnityAccess: No valid membership found.');
      return false;
    }
  }

  setActiveAuthState(authState: AuthState) {
    this.store.update(
      setProps({
        authState
      })
    );
  }

  clearActiveAuthState() {
    this.store.update(setProps({ authState: undefined }));
  }

  /** When no configured default route matches the user's roles. */
  private static readonly noDefaultAccessUrl = '/auth/403';

  /**
   * First permitted destination for the signed-in user (DIP dashboard → … → user management).
   */
  getDefaultPostLoginUrl(): string {
    const authState = this.authState;
    if (!authState?.userRoles?.length) {
      return ActiveSecurityContextStateService.noDefaultAccessUrl;
    }
    const resolved = resolveDefaultPostLoginPath(authState.userRoles as UserRole[]);
    return resolved || ActiveSecurityContextStateService.noDefaultAccessUrl;
  }

  /**
   * Resolves the most appropriate route for the current user and situation.
   */
  redirectToBestRoute() {
    this.fullScreenLoaderService.show();
    this.navigateToUrl(this.getDefaultPostLoginUrl());
    this.fullScreenLoaderService.hide();
  }

  navigateHome() {
    this.navigateToUrl(this.getDefaultPostLoginUrl());
  }

  private navigateToUrl(url: string): void {
    if (!this.router) {
      this.router = this.injector.get<Router>(Router);
    }
    void this.router.navigateByUrl(url);
  }

  ngOnDestroy(): void {
    this.store.destroy();
  }

}
